import { LoginService } from '../../services/login.service';
import { map, switchMap, tap, shareReplay } from 'rxjs/operators';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Observable, forkJoin, Subscription } from 'rxjs';
import { Component, OnInit, ViewEncapsulation, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { ReportingService } from '../../services/reporting.service';
import { IndividualBriefReport, IndividualDetailReport } from '../../models/reporting';
import { ConfigService } from '../../services/config.service';
import * as FileSaver from 'file-saver';
import { LanguageService } from '../../services/language.service';

const stylePathForPdfGenerator = 'assets\\styles\\pdf_css\\';
const bootstrapStyleName = 'bootstrap.min.css';
const commonStyleName = 'styles.css';
const briefReportStyleName = 'brief-report.css';
@Component({
    selector: 'app-reporting',
    templateUrl: './reporting.component.html',
    styleUrls: [],
    encapsulation: ViewEncapsulation.None
})


export class ReportingComponent implements OnInit, OnDestroy {
    @ViewChild('individualReportContent') individualReportContent: ElementRef;
    @ViewChild('detailedReportContent') detailedReportContent: ElementRef;
    individualBriefReport$: Observable<IndividualBriefReport>;
    individualDetailedReport$: Observable<IndividualDetailReport>;
    processId: number;
    appraiseeId: number;
    styleContent: string;
    isSpinnerEnabled = false;
    isDetailedReport = false;

    private readonly decimalScale = 10;
    private endOfSummaryReportName = '_summary.pdf';
    private endOfDetailedReportName = '_detailed.pdf';
    private individualReport: IndividualBriefReport;
    private reportsSubscription: Subscription = new Subscription();
    constructor(private reportingService: ReportingService,
        private readonly route: ActivatedRoute,
        private readonly configServece: ConfigService,
        private readonly languageService: LanguageService,
        private readonly loginService: LoginService) {
        this.styleContent = `<link rel=\"stylesheet\" type=\"text / css\" href=\"${stylePathForPdfGenerator + bootstrapStyleName}"> 
        <link rel=\"stylesheet\" type=\"text / css\" href=\"${stylePathForPdfGenerator + commonStyleName}\">
        <link rel=\"stylesheet\" type=\"text / css\" href=\"${stylePathForPdfGenerator + briefReportStyleName}\">`;
    }

    ngOnInit() {
        this.changeLanguage();
        this.subscribeIndividualReportDownload();
        this.subscribeDetailedReporDownload();
        this.getIndividualReport();
        this.getDetailedReport();
        this.reportsSubscription.add(this.reportingService.detailedReportClicked$
        .subscribe(x => this.isDetailedReport = x));
    }

    ngOnDestroy() {
        this.reportsSubscription.unsubscribe();
    }

    getIndividualReportData(individualBriefReport: IndividualBriefReport) {
        this.individualReport = individualBriefReport;
    }

    private changeLanguage() {
         this.languageService.languageChanged$
            .subscribe(selectedLanguage => {        
                this.isDetailedReport = false;
                this.reportingService.swipeReport(this.isDetailedReport);
                this.individualBriefReport$ = this.reportingService
                .getIndividualBriefReport(this.processId, this.appraiseeId);
            });
    }

    private getIndividualReport() {
        this.individualBriefReport$ = this.getRouteData()
            .pipe(
                switchMap(
                    p => {
                        return this.reportingService
                            .getIndividualBriefReport(p.processId, p.appraiseeId);
                    }
                )
            );
    }

    private getDetailedReport() {
        this.individualDetailedReport$ = this.getRouteData().pipe(
            switchMap(
                p => {
                    return this.reportingService
                    .getIndividualDetailedReport(p.processId, p.appraiseeId);
                }
            )
        );
    }

    private getHtmlForIndividualReport(): string {
        const hideCanvas = /(<canvas)/ig;
                const showImg = /(hidden="")/ig;
        return this.individualReportContent.nativeElement.innerHTML
                    .replace(showImg, '').replace(hideCanvas, '<canvas hidden');
    }

    private getRouteData(): Observable<{processId: number, appraiseeId: number}> {
        return this.route.paramMap
        .pipe(
            map(
                (p: ParamMap) => {
                    const result = {
                        processId: parseInt(p.get('processId'), this.decimalScale),
                        appraiseeId: parseInt(p.get('appraiseeId'), this.decimalScale)
                    };
                    if (result.appraiseeId) {
                        return result;
                    }
                    result.appraiseeId = this.loginService.getUser.id;
                    return result;
                }
            ),
            tap(result => {
                this.processId = result.processId;
                this.appraiseeId = result.appraiseeId;
            }));
    }

    private subscribeIndividualReportDownload() {
        this.reportsSubscription.add(this.reportingService.downloadIndividualPdfClicked$
        .subscribe(x => {
            this.isSpinnerEnabled = true;
            const htmlForIndividualReportPdf = this.getHtmlForIndividualReport();
            if (!htmlForIndividualReportPdf) {
                return;
            }
            this.reportingService.generatePdf(this.styleContent + htmlForIndividualReportPdf)
                .subscribe(blob => {
                    FileSaver.saveAs(blob, this.individualReport.employeeInfo.displayName.replace(', ', '_') + this.endOfSummaryReportName);
                    this.isSpinnerEnabled = false;
                    this.reportingService.onIndividualPdfDownloaded();
                });

        }));
    }

    private subscribeDetailedReporDownload() {
        this.reportsSubscription.add(this.reportingService.downloadSummaryPdfClicked$
            .subscribe(x => {
                this.isSpinnerEnabled = true;
                const htmlForIndividualReportPdf = this.getHtmlForIndividualReport();
                const htmlForDetailedPdf: string = this.detailedReportContent ? 
                this.detailedReportContent.nativeElement.innerHTML : undefined;
                forkJoin([ this.reportingService.generatePdf(this.styleContent + htmlForIndividualReportPdf),
                    this.reportingService.generatePdf(this.styleContent + htmlForDetailedPdf)
                ])
                .subscribe(generatedBlobs => {
                    FileSaver
                        .saveAs(generatedBlobs[0],
                            this.getEmployeeName() + this.endOfSummaryReportName);
                    FileSaver
                    .saveAs(generatedBlobs[1], 
                        this.getEmployeeName() + this.endOfDetailedReportName);
                    this.isSpinnerEnabled = false;
                    this.reportingService.onSummaryPdfDownloaded();
                });
            }));
    }

    private getEmployeeName() {
        return this.individualReport.employeeInfo ? this.individualReport.employeeInfo.displayName.replace(', ', '_') : '';

    }
}
